<template>
  <div>
    <confirmation-dialog
      v-model="showSpouseUserConfirmation"
      @confirm="validateForm(true)"
      type="info"
    >
      <template v-if="existingUser">
        User
        <b
          >{{ existingUser.fname }}, {{ existingUser.lname }} ({{
            existingUser.email
          }})</b
        >
        already exists in the system, do you want to assign it as the spouse?
      </template>
    </confirmation-dialog>

    <v-dialog
      v-model="missingUsernameWarningDialog"
      width="500"
      @keydown.esc="missingUsernameWarningDialog = false"
    >
      <v-card block flat>
        <v-card-title class="pb-0">
          <span v-if="signInWithPhoneNumberEnabled" class="title"
            >Blank Email and Phone Number Confirmation</span
          >
          <span v-else class="title">Blank Email Confirmation</span>
          <v-spacer />
        </v-card-title>
        <v-card-text>
          <v-alert class="text-xs-left" type="info" :value="true" outline>
            {{
              signInWithPhoneNumberEnabled
                ? "You are adding a client without an email or a phone number, he or she won't be able to login or receive notifications until you add an email or a phone number."
                : "You are adding a client without an email, he or she won't be able to login or receive notifications until you add an email."
            }}<br />
            Do you want to proceed?
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="text-capitalize primary" @click="addClient(true)">
            Yes
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="addUsername"
          >
            {{
              signInWithPhoneNumberEnabled
                ? 'Add email or phone number'
                : 'Add email'
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="openDialog"
      width="800"
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="openDialog = false"
    >
      <v-card block flat>
        <v-card-title class="pb-0">
          <span class="title">Add New Client</span>
          <v-spacer />
          <span class="caption error--text">*Required</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-alert
              class="mb-4 text-xs-left"
              type="info"
              :value="true"
              outline
            >
              You will be assigned as the client's contact point. You can change
              the assigned firm user after adding the client.
            </v-alert>
            <v-layout column id="add-client-form">
              <v-layout wrap class="text-xs-left">
                <v-flex xs4 :xs6="!advancedClient" class="px-2">
                  <div>First Name<span class="error--text ml-1">*</span></div>
                  <v-text-field
                    name="first-name"
                    placeholder="John"
                    type="text"
                    v-model="newClientFirstName"
                    :disabled="!!newClientObject"
                    required
                    :rules="rules.presence"
                    :error-messages="firstNameErrors"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 :xs6="!advancedClient" class="px-2">
                  <div>Last Name<span class="error--text ml-1">*</span></div>
                  <v-text-field
                    name="last-name"
                    placeholder="Smith"
                    type="text"
                    v-model="newClientLastName"
                    :error-messages="lastNameErrors"
                    required
                    :rules="rules.presence"
                    :disabled="!!newClientObject"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 v-if="advancedClient" class="px-2">
                  <div>Preferred Name</div>
                  <v-text-field
                    name="preferred-name"
                    placeholder="Johnny"
                    type="text"
                    v-model="newPreferredName"
                    :disabled="!!newClientObject"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 :xs6="!advancedClient" class="px-2">
                  <div>Email</div>
                  <v-text-field
                    name="email"
                    :placeholder="forTesting ? 'Test Email' : 'Email'"
                    type="email"
                    v-model="$v.newClientEmail.$model"
                    :error="$v.newClientEmail.$error"
                    :error-messages="
                      $v.newClientEmail.$error ? ['E-mail must be valid'] : []
                    "
                    :disabled="!!newClientObject"
                    class="default-input"
                    ref="userEmail"
                    solo
                  />
                </v-flex>
                <v-flex xs4 :xs6="!advancedClient" class="px-2">
                  <div>Phone number</div>
                  <v-text-field
                    name="phone-number"
                    type="phone"
                    mask="phone"
                    placeholder="Optional"
                    v-model="newClientPhoneNumber"
                    :disabled="!!newClientObject"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 :xs6="!advancedClient" class="px-2">
                  <div>Entity Type<span class="error--text ml-1">*</span></div>
                  <v-combobox
                    v-model="newType"
                    :items="newTypes"
                    placeholder="Select the entity"
                    :disabled="!!newClientObject"
                    :error-messages="typeErrors"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex
                  xs4
                  :xs6="!advancedClient"
                  class="px-2"
                  v-if="!isIndividualType"
                >
                  <div>Entity Name</div>
                  <v-text-field
                    name="company-name"
                    v-if="newType !== 'Individual'"
                    placeholder="e.g. VentureScalers"
                    type="text"
                    v-model="newCompanyName"
                    :disabled="!!newClientObject"
                    required
                    :rules="duplicateExistenceRules"
                    :error-messages="newCompanyNameErrors"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 :xs6="!advancedClient" class="px-2" v-else>
                  <div>Entity Name<span class="error--text ml-1">*</span></div>
                  <v-text-field
                    name="company-name"
                    type="text"
                    disabled
                    :error-messages="
                      duplicateRules(
                        newClientLastName + ', ' + newClientFirstName,
                      )
                    "
                    :value="newIndividualName"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <template v-if="advancedClient">
                  <v-flex xs4 class="px-2">
                    <div>
                      {{ isIndividualType ? 'Work' : 'Business' }} Email
                    </div>
                    <v-text-field
                      placeholder="Optional"
                      v-model="newEntityBusinessEmail"
                      :disabled="!!newClientObject"
                      solo
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs4 class="px-2" v-if="!isIndividualType">
                    <div>Other Email</div>
                    <v-text-field
                      placeholder="Optional"
                      v-model="newEntityOtherEmail"
                      :disabled="!!newClientObject"
                      solo
                      class="default-input"
                    />
                  </v-flex>
                </template>
                <template v-if="advancedClient">
                  <v-flex xs4 class="px-2">
                    <div>Work Phone number</div>
                    <v-layout>
                      <v-flex xs8 class="pr-1">
                        <v-text-field
                          name="phone-number"
                          type="phone"
                          mask="phone"
                          placeholder="Optional"
                          v-model="newClientWorkPhoneNumber"
                          :disabled="!!newClientObject"
                          solo
                          class="default-input"
                        />
                      </v-flex>
                      <v-flex xs4 class="pl-1">
                        <v-text-field
                          placeholder="Ext."
                          v-model="newClientWorkPhoneExtension"
                          :disabled="!!newClientObject"
                          solo
                          class="default-input"
                        />
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs4 class="px-2">
                    <div>Contact Method</div>
                    <v-select
                      class="default-input"
                      solo
                      item-value="value"
                      item-text="label"
                      placeholder="Ask Client"
                      :items="preferredContactMethods"
                      v-model="newClientPreferredContactMethod"
                      :disabled="!!newClientObject"
                    />
                  </v-flex>
                  <v-flex xs4 class="px-2">
                    <div>Tax Software Return ID</div>
                    <v-text-field
                      class="default-input"
                      solo
                      placeholder="Input if return is already created"
                      v-model="newReturnId"
                      :disabled="!!newClientObject"
                    />
                  </v-flex>
                  <v-flex xs5 class="px-2">
                    <div>Time Zone</div>
                    <v-select
                      class="default-input"
                      solo
                      :items="timeZones"
                      :disabled="!!newClientObject"
                      v-model="newClientTimeZone"
                    />
                  </v-flex>
                  <v-flex xs8 class="px-2">
                    <div>Street Address</div>
                    <v-text-field
                      solo
                      :disabled="!!newClientObject"
                      label="Street Address"
                      v-model="newEntityStreet"
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs4 class="px-2">
                    <div>Country</div>
                    <v-autocomplete
                      solo
                      :disabled="!!newClientObject"
                      label="Country"
                      :items="otherCountries"
                      v-model="newEntityCountry"
                      class="default-input"
                    />
                  </v-flex>
                </template>
              </v-layout>
              <v-layout class="text-xs-left" v-if="advancedClient">
                <v-flex xs4 class="px-2">
                  <div>City</div>
                  <v-text-field
                    solo
                    label="City"
                    :disabled="!!newClientObject"
                    v-model="newEntityCity"
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>
                    {{ isEmpty(newEntityCountry) ? 'State' : 'Province/State' }}
                  </div>
                  <v-text-field
                    v-if="newEntityCountry"
                    solo
                    label="State"
                    v-model="newEntityState"
                    class="default-input"
                    placeholder="Province or State"
                  />
                  <v-autocomplete
                    v-else
                    solo
                    hide-selected
                    label="State"
                    :disabled="!!newClientObject"
                    v-model="newEntityState"
                    class="default-input"
                    :items="states"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>
                    {{ isEmpty(newEntityCountry) ? 'Zip Code' : 'Postal Code' }}
                  </div>
                  <v-text-field
                    solo
                    label="Zip"
                    :disabled="!!newClientObject"
                    v-model="newEntityZip"
                    class="default-input"
                  />
                </v-flex>
              </v-layout>

              <template v-if="isIndividualType">
                <v-layout wrap class="text-xs-left" v-if="newClientHasSpouse">
                  <v-flex xs6 class="px-2">
                    <div>
                      Spouse First Name<span class="error--text ml-1">*</span>
                    </div>
                    <v-text-field
                      solo
                      placeholder="First Name"
                      v-model="newSpouseFirstName"
                      :error-messages="spouseFirstNameErrors"
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs6 class="px-2">
                    <div>Spouse Last Name</div>
                    <v-text-field
                      solo
                      placeholder="Last Name"
                      v-model="newSpouseLastName"
                      class="default-input"
                    />
                  </v-flex>
                  <div
                    class="advanced-client"
                    :class="advancedClient ? 'advanced-client--visible' : ''"
                  >
                    <v-flex xs4 class="px-2">
                      <div>Spouse Phone Number</div>
                      <v-text-field
                        solo
                        placeholder="Optional"
                        v-model="newSpousePhoneNumber"
                        class="default-input"
                        mask="phone"
                      />
                    </v-flex>
                    <v-flex xs4 class="px-2">
                      <div>Spouse Email</div>
                      <v-text-field
                        solo
                        placeholder="Optional"
                        v-model="$v.newSpouseEmail.$model"
                        :error="$v.newSpouseEmail.$error"
                        :error-messages="
                          $v.newSpouseEmail.$error
                            ? ['E-mail must be valid']
                            : []
                        "
                        class="default-input"
                      />
                    </v-flex>
                    <v-flex xs4 class="px-2">
                      <div>Spouse Work Email</div>
                      <v-text-field
                        solo
                        placeholder="Optional"
                        v-model="newSpouseWorkEmail"
                        class="default-input"
                      />
                    </v-flex>
                    <v-flex xs4 class="px-2">
                      <div>
                        Spouse is Taxpayer
                        <v-tooltip top>
                          <v-icon size="16" slot="activator">info</v-icon>
                          <span>Select if the spouse is the taxpayer</span>
                        </v-tooltip>
                      </div>
                      <v-switch
                        class="default-input ma-0 pa-0"
                        solo
                        :label="spouseIsTaxpayer ? 'Yes' : 'No'"
                        v-model="spouseIsTaxpayer"
                      />
                    </v-flex>
                    <v-flex xs4 class="pr-2">
                      <div>
                        Needs dashboard access
                        <v-tooltip top>
                          <v-icon size="16" slot="activator">info</v-icon>
                          <span
                            >Needs spouse email and email must be different than
                            primary contact email</span
                          >
                        </v-tooltip>
                      </div>
                      <v-switch
                        class="default-input ma-0 pa-0"
                        :disabled="!enableDashboardAccess"
                        solo
                        :label="spouseNeedsAccess ? 'Yes' : 'No'"
                        v-model="spouseNeedsAccess"
                      />
                    </v-flex>
                  </div>
                </v-layout>
              </template>

              <v-layout class="text-xs-left">
                <v-flex
                  xs4
                  class="px-2"
                  v-if="advancedClient && externalEntityIdAllowed"
                >
                  <div>External Entity Id</div>
                  <v-text-field
                    solo
                    label="Entity Id"
                    v-model="externalEntityId"
                    class="default-input"
                    @keydown.native.32.stop.prevent
                  />
                </v-flex>

                <v-flex
                  xs4
                  class="px-2"
                  v-if="advancedClient && externalUserIdAllowed"
                >
                  <div>External User Id</div>
                  <v-text-field
                    solo
                    label="User Id"
                    v-model="externalUserId"
                    class="default-input"
                    @keydown.native.32.stop.prevent
                  />
                </v-flex>
                <v-flex xs4 class="px-2" v-if="hasFirmGroups">
                  <v-layout class="text-xs-left">
                    Assigned Group<span class="error--text ml-1">*</span>
                  </v-layout>
                  <v-select
                    type="text"
                    :items="currentFirm.firm_groups"
                    placeholder="Select group"
                    v-model="selectedGroupId"
                    solo
                    class="default-input"
                    :disabled="
                      currentUser.firm_access === 'group' || !!newClientObject
                    "
                    :error-messages="groupSelectErrorMessage"
                    item-text="name"
                    item-value="id"
                    required
                  />
                </v-flex>
                <v-flex :xs6="!advancedClient" xs4 class="px-2">
                  <v-layout class="text-xs-left">
                    Assigned To<span
                      class="error--text ml-1"
                      v-if="hasFirmGroups"
                      >*</span
                    >
                  </v-layout>
                  <v-select
                    class="default-input body-1"
                    solo
                    :items="filteredFirmUsers"
                    v-model="assignedUserId"
                    :error-messages="assignedToErrorMessage"
                    placeholder="Select a user"
                    item-text="name"
                    item-value="id"
                  />
                </v-flex>
              </v-layout>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-card-actions class="add-client-buttons">
          <v-switch
            v-if="isIndividualType"
            class="default-input my-0 py-0 pr-3"
            v-model="newClientHasSpouse"
            hide-details
            label="Has Spouse?"
          />
          <v-switch
            class="default-input my-0 py-0 pr-3"
            solo
            hide-details
            label="Detailed Attributes"
            :disabled="!!newClientObject"
            v-model="advancedClient"
          />
          <v-tooltip bottom>
            <v-switch
              class="default-input my-0 py-0 mr-2"
              solo
              hide-details
              label="Test Client"
              slot="activator"
              v-model="forTesting"
            />
            <span>Test clients will not count towards credit usage</span>
          </v-tooltip>
          <v-spacer />
          <template
            v-if="
              currentFirm &&
                currentFirm.tax_softwares &&
                currentFirm.tax_softwares.length > 1
            "
          >
            <div>Tax Software</div>
            <v-select
              v-model="selectedTaxSoftware"
              :items="currentFirm.tax_softwares"
              solo
              hide-details
              :disabled="!!newClientObject"
              class="default-input mx-2"
              style="max-width: 150px"
            />
          </template>

          <v-btn
            class="primary text-none"
            :loading="creatingClient"
            @click="validateForm()"
          >
            Add Client
          </v-btn>
          <v-btn class="error--text text-none" flat @click="openDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import API from '@/api';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { mapGetters, mapState } from 'vuex';
import {
  rules,
  states,
  defaultEntityTypes,
  preferredContactMethods,
  yearOptions,
  timeZones,
  otherCountries,
  emailFormatter,
} from '@/constants/variables';
import UtilsMixins from '../../mixins/UtilsMixins';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import { email } from 'vuelidate/lib/validators';
import ConfirmationDialog from '../ConfirmationDialog';

export default {
  name: 'CreateClientDialog',
  components: {
    ConfirmationDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    firmUsers: {
      type: Array,
    },
    newTypes: {
      type: Array,
      default: defaultEntityTypes,
    },
  },
  mixins: [UtilsMixins, FirmPreferenceMixins],
  watch: {
    value(newVal) {
      this.openDialog = newVal;
    },
    openDialog(newVal) {
      this.$emit('input', newVal);
      if (newVal) {
        this.resetForm();
        this.advancedClient = !!this.defaultAdvancedClient;
      }
    },
  },
  data() {
    return {
      otherCountries,
      rules,
      states,
      timeZones,
      yearOptions,
      preferredContactMethods,
      showSpouseUserConfirmation: false,
      existingUser: null,
      spouseFirstNameErrors: [],
      groupSelectErrorMessage: [],
      assignedToErrorMessage: [],
      firstNameErrors: [],
      newCompanyNameErrors: [],
      lastNameErrors: [],
      typeErrors: [],
      newClientObject: null,
      advancedClient: false,
      forTesting: false,
      openDialog: false,
      newClientFirstName: null,
      newClientLastName: null,
      newType: null,
      newPreferredName: null,
      missingUsernameWarningDialog: false,
      creatingClient: false,
      newCompanyName: null,
      newClientEmail: null,
      newEntityBusinessEmail: null,
      newEntityOtherEmail: null,
      newClientPhoneNumber: null,
      newClientWorkPhoneNumber: null,
      newClientWorkPhoneExtension: null,
      newClientPreferredContactMethod: null,
      newClientTimeZone: 'Pacific Time (US & Canada)',
      selectedTaxSoftware: null,
      selectedGroupId: null,
      assignedUserId: null,
      newEntityStreet: null,
      newEntityCountry: null,
      newEntityCity: null,
      newEntityState: null,
      newEntityZip: null,
      newClientHasSpouse: false,
      newSpouseFirstName: null,
      newSpouseLastName: null,
      spouseIsTaxpayer: false,
      newSpousePhoneNumber: null,
      newSpouseEmail: null,
      newSpouseWorkEmail: null,
      spouseNeedsAccess: false,
      externalEntityId: null,
      externalUserId: null,
      newReturnId: null,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
    filteredFirmUsers() {
      return this.hasFirmGroups
        ? this.firmUsers.filter(
            user =>
              user.access === 'partner' ||
              (user.group && user.group.id === this.selectedGroupId),
          )
        : this.firmUsers;
    },
    enableDashboardAccess() {
      return (
        this.newSpouseEmail &&
        !this.$v.newSpouseEmail.$error &&
        this.newSpouseEmail !== this.newClientEmail
      );
    },
    duplicateExistenceRules() {
      return [
        v => !!v || 'This field is required',
        v =>
          (v &&
            this.isEmpty(
              this.companies.filter(
                c => c.name.toLowerCase() === v.toLowerCase(),
              ),
            )) ||
          'Duplicate Entity Detected',
        v =>
          (v && !v.includes('/')) ||
          "You cannot have slash (/) in the entity's name",
      ];
    },
    newIndividualName() {
      return `${this.capitalizeWords(
        this.newClientLastName,
      )}, ${this.capitalizeWords(this.newClientFirstName)}`;
    },
    isIndividualType() {
      return this.newType === 'Individual';
    },
    spouseSavable() {
      if (this.isIndividualType && this.newClientHasSpouse) {
        return !isEmpty(this.newSpouseFirstName);
      }
      return true;
    },
  },
  validations: {
    newClientEmail: {
      email: val => email(emailFormatter(val)),
    },
    newSpouseEmail: {
      email: val => email(emailFormatter(val)),
    },
  },
  methods: {
    isEmpty,
    get,
    addUsername() {
      this.missingUsernameWarningDialog = false;
      this.$refs.userEmail.$refs.input.focus();
    },
    duplicateRules(value) {
      if (
        !this.isEmpty(
          this.companies.filter(
            c => c.name.toLowerCase() === value.toLowerCase(),
          ),
        )
      ) {
        return 'Duplicate Entity Detected';
      }
    },
    async validateForm(confirmed) {
      const newClientFilledOut =
        this.newType &&
        this.newClientFirstName &&
        this.newClientLastName &&
        this.spouseSavable &&
        (this.isIndividualType ||
          (!!this.newCompanyName && !this.newCompanyName.includes('/')));

      this.spouseFirstNameErrors = this.spouseSavable
        ? []
        : ['This field is required'];

      if (
        !newClientFilledOut ||
        (this.currentFirm &&
          this.hasFirmGroups &&
          this.currentUser.firm_access !== 'group' &&
          (!this.selectedGroupId || !this.assignedUserId)) ||
        this.$v.newClientEmail.$error ||
        (this.newClientHasSpouse && this.$v.newSpouseEmail.$error)
      ) {
        const validateGroups =
          this.hasFirmGroups && this.currentUser.firm_access !== 'group';

        if (validateGroups && !this.selectedGroupId) {
          this.groupSelectErrorMessage = ['This field is required'];
        } else {
          this.groupSelectErrorMessage = [];
        }

        if (validateGroups && !this.assignedUserId) {
          this.assignedToErrorMessage = ['This field is required'];
        } else {
          this.assignedToErrorMessage = [];
        }

        this.firstNameErrors = this.newClientFirstName
          ? []
          : ['This field is required'];
        this.lastNameErrors = this.newClientLastName
          ? []
          : ['This field is required'];
        this.typeErrors = this.newType ? [] : ['This field is required'];

        if (!this.isIndividualType && !this.newCompanyName) {
          this.newCompanyNameErrors = ['This field is required'];
        } else {
          this.newCompanyNameErrors = [];
        }
      } else {
        if (
          this.newClientHasSpouse &&
          this.newSpouseEmail &&
          this.newSpouseEmail !== this.newClientEmail &&
          !confirmed
        ) {
          const userResponse = await API.user.findUserByEmail(
            this.newSpouseEmail,
          );
          if (userResponse.data) {
            this.existingUser = userResponse.data;
            this.showSpouseUserConfirmation = true;

            return;
          }
        }

        if (
          this.newClientObject &&
          this.newClientHasSpouse &&
          !this.existingUser
        ) {
          this.createSpouseForExitingClient();
        } else if (
          this.newClientEmail ||
          (this.signInWithPhoneNumberEnabled && this.newClientPhoneNumber)
        ) {
          this.addClient();
        } else {
          this.missingUsernameWarningDialog = true;
        }
      }
    },
    closeOnSuccess() {
      this.openDialog = false;
      this.$store.dispatch('application/setSuccessNotification', {
        message: "You've just added a new client!",
      });
      this.$store.dispatch('companies/getCompanies');

      this.$emit('refresh-questionnaires');
      this.resetForm();
    },
    async addClient(useTempEmail) {
      try {
        this.missingUsernameWarningDialog = false;
        this.showSpouseUserConfirmation = false;
        this.creatingClient = true;
        if (this.isIndividualType) {
          this.newCompanyName = this.newIndividualName;
        }

        const response = await API.user.createClientWithAPI({
          email: this.newClientEmail,
          firstName: this.capitalizeWords(this.newClientFirstName),
          lastName: this.capitalizeWords(this.newClientLastName),
          preferredName: this.capitalizeWords(this.newPreferredName),
          entityType: this.newType,
          timeZone: this.newClientTimeZone,
          companyName: this.newCompanyName,
          phoneNumber: this.newClientPhoneNumber,
          workPhoneNumber: this.newClientWorkPhoneNumber,
          workPhoneExtension: this.newClientWorkPhoneExtension,
          street: this.newEntityStreet,
          city: this.newEntityCity,
          state: this.newEntityState,
          country: this.newEntityCountry,
          zip: this.newEntityZip,
          businessEmail: this.newEntityBusinessEmail,
          otherEmail: this.newEntityOtherEmail,
          preferredContactMethod: this.newClientPreferredContactMethod,
          groupId: this.selectedGroupId,
          forTesting: this.forTesting,
          taxSoftware: this.selectedTaxSoftware,
          externalEntityId: this.externalEntityId,
          externalUserId: this.externalUserId,
          advisorId: this.assignedUserId || undefined,
          taxReturnId: this.newReturnId,
          useTempEmail,
        });
        if (response.data.error || response.data.errors) {
          if (response.data.error.message === 'EMAIL_EXISTS') {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is already taken.',
            });
          } else if (response.data.error.message === 'INVALID_EMAIL') {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is invalid.',
            });
          } else {
            if (response.data.errors) {
              this.$store.dispatch('application/setErrorNotification', {
                message: response.data.errors,
              });
            } else {
              this.$store.dispatch('application/setErrorNotification', {
                message: response.data.error,
              });
            }
          }
        } else if (this.newClientHasSpouse) {
          this.newClientObject = response.data;
          if (this.existingUser) {
            await API.company.createSpouseAssigment({
              companyId: this.newClientObject.company_id,
              userId: this.existingUser.id,
            });
          } else {
            await this.createSpouse();
          }
          this.closeOnSuccess();
        } else {
          this.closeOnSuccess();
        }
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: e,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
        console.log(e);
      } finally {
        this.missingUsernameWarningDialog = false;
        this.creatingClient = false;
      }
    },
    async createSpouseForExitingClient() {
      try {
        this.creatingClient = true;
        await this.createSpouse();
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
        console.log(e);
      } finally {
        this.missingUsernameWarningDialog = false;
        this.creatingClient = false;
      }
    },
    async createSpouse() {
      const spouseResponse = await API.company.createSpouse({
        fname: this.newSpouseFirstName,
        lname: this.newSpouseLastName,
        email: this.newSpouseEmail,
        phone_number: this.newSpousePhoneNumber,
        work_email: this.newSpouseWorkEmail,
        is_taxpayer: this.spouseIsTaxpayer,
        companyId: this.newClientObject.company_id,
        needsAccess: this.spouseNeedsAccess,
      });

      if (spouseResponse.data.error || spouseResponse.data.errors) {
        if (spouseResponse.data.error.message === 'EMAIL_EXISTS') {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Spouse email is already taken.',
          });
        } else if (spouseResponse.data.error.message === 'INVALID_EMAIL') {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Spouse email is invalid.',
          });
        } else {
          if (spouseResponse.data.errors) {
            this.$store.dispatch('application/setErrorNotification', {
              message: spouseResponse.data.errors,
            });
          } else {
            this.$store.dispatch('application/setErrorNotification', {
              message: spouseResponse.data.error,
            });
          }
        }
      } else {
        this.closeOnSuccess();
      }
    },
    resetForm() {
      this.$refs.form.resetValidation();
      this.groupSelectErrorMessage = [];
      this.firstNameErrors = [];
      this.lastNameErrors = [];
      this.typeErrors = [];
      this.newCompanyNameErrors = [];
      this.newClientObject = null;
      this.newType = null;
      this.newClientFirstName = null;
      this.newClientLastName = null;
      this.newCompanyName = null;
      this.newClientEmail = null;
      this.newPreferredName = null;
      this.advancedClient = !!this.defaultAdvancedClient;
      this.selectedClient = null;
      this.newClientPhoneNumber = null;
      this.newClientWorkPhoneNumber = null;
      this.newClientTimeZone = this.currentFirm.time_zone;
      this.newClientWorkPhoneExtension = null;
      this.newEntityStreet = null;
      this.newEntityCity = null;
      this.newEntityState = null;
      this.newEntityCountry = null;
      this.newEntityZip = null;
      this.newClientEin = null;
      this.newClientHasSpouse = false;
      this.newEntityBusinessEmail = null;
      this.newEntityOtherEmail = null;
      this.newSpouseFirstName = null;
      this.newSpouseLastName = null;
      this.newSpouseEmail = null;
      this.newSpousePhoneNumber = null;
      this.newSpouseWorkEmail = null;
      this.spouseIsTaxpayer = false;
      this.forTesting = false;
      this.spouseNeedsAccess = false;
      this.selectedGroupId =
        this.currentUser.firm_access === 'group'
          ? this.currentUser.firm_group.id
          : null;
      this.showSpouseUserConfirmation = false;
      this.existingUser = null;
      this.newReturnId = null;
    },
  },
  created() {
    this.openDialog = this.value;
  },
};
</script>

<style>
.v-messages__message {
  line-height: 12px;
}

.advanced-client {
  display: none;
  flex-wrap: wrap;
}

.advanced-client--visible {
  display: flex;
}
</style>
